import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../ui/Icon';
import { uiActions } from '../../store';
import { searchTools, TOOL_CATEGORIES } from '../../data/tools';

const CommandPalette = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { commandPaletteOpen } = useSelector(s => s.ui);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);

  const results = query.length > 0 ? searchTools(query) : searchTools('').slice(0, 8);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        dispatch(uiActions.setCommandPalette(true));
      }
      if (e.key === 'Escape') {
        dispatch(uiActions.setCommandPalette(false));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [dispatch]);

  useEffect(() => {
    if (commandPaletteOpen) {
      setQuery('');
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [commandPaletteOpen]);

  useEffect(() => { setSelected(0); }, [query]);

  const handleSelect = (tool) => {
    dispatch(uiActions.setCommandPalette(false));
    navigate(`/tool/${tool.id}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)); }
    if (e.key === 'Enter' && results[selected]) { handleSelect(results[selected]); }
  };

  if (!commandPaletteOpen) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-start justify-center pt-[20vh]">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => dispatch(uiActions.setCommandPalette(false))} />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -10 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-xl mx-4 rounded-2xl overflow-hidden"
          style={{ background: 'var(--bg-primary)', boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border-light)' }}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-5 py-4" style={{ borderBottom: '1px solid var(--border-light)' }}>
            <Icon name="Search" size={20} style={{ color: 'var(--text-muted)' }} />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search tools, conversions, features..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent outline-none text-base"
              style={{ color: 'var(--text-primary)' }}
            />
            <kbd className="text-xs px-2 py-1 rounded-md font-mono"
              style={{ background: 'var(--bg-tertiary)', color: 'var(--text-muted)' }}>ESC</kbd>
          </div>

          {/* Results */}
          <div className="max-h-80 overflow-y-auto py-2">
            {results.length === 0 ? (
              <div className="px-5 py-8 text-center" style={{ color: 'var(--text-muted)' }}>
                <Icon name="Search" size={32} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">No tools found for "{query}"</p>
              </div>
            ) : (
              results.map((tool, i) => {
                const cat = TOOL_CATEGORIES[tool.category];
                return (
                  <button
                    key={tool.id}
                    onClick={() => handleSelect(tool)}
                    onMouseEnter={() => setSelected(i)}
                    className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${
                      i === selected ? '' : ''
                    }`}
                    style={{
                      background: i === selected ? 'var(--bg-secondary)' : 'transparent',
                    }}
                  >
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: cat?.bgLight, color: cat?.color }}>
                      <Icon name={tool.icon} size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{tool.name}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{tool.desc}</p>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-md flex-shrink-0"
                      style={{ background: cat?.bgLight, color: cat?.color }}>
                      {cat?.label}
                    </span>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-3 flex items-center gap-4 text-xs"
            style={{ borderTop: '1px solid var(--border-light)', color: 'var(--text-muted)' }}>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-tertiary)' }}>↑↓</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-tertiary)' }}>↵</kbd> Select</span>
            <span className="flex items-center gap-1"><kbd className="px-1.5 py-0.5 rounded" style={{ background: 'var(--bg-tertiary)' }}>esc</kbd> Close</span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CommandPalette;
